import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy,
    ElementRef,
    AfterViewInit,
    ViewChild
} from '@angular/core';
import { Subscription, take } from 'rxjs';
import { MessageService } from 'primeng/api';

import { ColorModel } from '../../models/color.model';
import { FavoriteColorsService, MAX_FAVORITE_COLORS } from './service/favorite-colors.service';
import { DropdownOptionModel } from '../../models/dropdown-option.model';
import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { RecentColorsService } from './service/recent-colors.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { BrandIdentityService } from '../../../BrandIdentity/services/brand-identity.service';
import { BrandIdentity } from '../../../BrandIdentity/models/brand-identity';
import { ColorPickerSourceType } from '../../enums/color-picker-source-types.enum';
import { ColorPickerCloseRequestType } from '../../enums/color-picker-close-request-types.enum';
import { ColorChangeModel } from '../../models/color-change.model';

enum SwatchCollectionType {
    RECENT = 'RECENT',
    FAVORITES = 'FAVORITES',
    BRAND = 'BRAND'
}

interface CloseEvent {
    closeRequestType: ColorPickerCloseRequestType;
    needClose: boolean;
}

const COLOR_PICKER_SESSION_STORAGE_KEY = 'agpr-color-picker-swatch-type';

@Component({
    selector: 'agpr-advanced-color-picker-popover',
    templateUrl: './advanced-color-picker-popover.component.html',
    styleUrls: ['./advanced-color-picker-popover.component.scss'],
    standalone: false
})
export class AdvancedColorPickerPopoverComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() value: ColorModel;
    @Input() property: string;
    @Input() showRecentColors: boolean = true;
    @Input() id: string = 'colorPickerPopover';
    @Input() showFavoriteColors = true;
    @Input() brandId?: number;

    @Output() colorChange = new EventEmitter<ColorChangeModel>();
    @Output() close = new EventEmitter<CloseEvent>();
    @Output() favoriteColorsChange = new EventEmitter<string[]>();

    public readonly MAX_FAVORITE_COLORS = MAX_FAVORITE_COLORS;
    public readonly swatchCollectionType = SwatchCollectionType;
    public hexInputValue: string = '';

    colorModel: ColorModel;
    selectedSwatchModel: DropdownOptionModel | null = null;
    selectedSwatchType: SwatchCollectionType = SwatchCollectionType.FAVORITES;

    favoriteColors: string[] = [];
    buttonTypes = ButtonTypesEnum;
    swatchTypeOptions: DropdownOptionModel[] = [];
    recentColors: string[] = [];

    brand: BrandIdentity | null = null;
    brandColors: string[] = [];
    container: HTMLDivElement;

    private subscriptions: Subscription[] = [];

    private readonly drowdownFulloptions = {
        [SwatchCollectionType.RECENT]: {
            label: 'ColorPicker.recentColors',
            value: SwatchCollectionType.RECENT
        },
        [SwatchCollectionType.FAVORITES]: {
            label: 'ColorPicker.favorites',
            value: SwatchCollectionType.FAVORITES
        },
        [SwatchCollectionType.BRAND]: {
            label: 'ColorPicker.brand',
            value: SwatchCollectionType.BRAND
        }
    };

    @ViewChild('container') containerViewChild: ElementRef;

    constructor(
        private favoriteColorsService: FavoriteColorsService,
        private recentColorsService: RecentColorsService,
        private messageService: MessageService,
        private brandIdentityService: BrandIdentityService
    ) {}

    ngOnInit(): void {
        // Initialize color model
        if (this.value) {
            this.colorModel = this.value.clone();
        } else {
            this.colorModel = ColorModel.createFromHex('#000000');
        }
        this.hexInputValue = this.colorModel.hex;

        // Set up dropdown options
        this.setupSwatchTypeOptions();

        // Load saved swatch type preference from session storage
        this.loadSwatchTypePreference();

        // Favorite colors: the color switcher should remain the same while open
        this.subscriptions.push(
            this.favoriteColorsService.getSyncedFavoriteColors$().subscribe((colors) => {
                this.favoriteColors = colors;
            }),
            this.brandIdentityService.all$.subscribe(brands => {
                this.brand = brands?.find(brand => brand.id === this.brandId) ?? null;
                this.brandColors = [...(this.brand?.colors ?? [])]
                    .sort((a, b) => Number(b.favorite) - Number(a.favorite))
                    .map(color => color.color);
            })
        );

        // check recent colors
        this.checkRecentColors();
    }

    ngAfterViewInit(): void {
        this.container = <HTMLDivElement>this.containerViewChild.nativeElement;
    }

    ngOnDestroy(): void {
        this.saveSwatchTypePreference();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private checkRecentColors(): void {
        if (this.showRecentColors) {
            this.recentColorsService.getRecentColors(this.property, this.hexInputValue)
                .pipe(take(1))
                .subscribe(colors => {
                    this.recentColors = colors;
                });
        }
    }

    private setupSwatchTypeOptions(): void {
        this.swatchTypeOptions = [];

        if (this.showRecentColors) {
            const recentOption = this.drowdownFulloptions[SwatchCollectionType.RECENT];
            this.swatchTypeOptions.push(recentOption);
            this.onSwatchTypeChange(recentOption);
        }

        const favoriteOption = this.drowdownFulloptions[SwatchCollectionType.FAVORITES];
        this.onSwatchTypeChange(favoriteOption);
        this.swatchTypeOptions.push(favoriteOption);

        if (this.brandId !== undefined && this.brandId !== null) {
            const brandOption = this.drowdownFulloptions[SwatchCollectionType.BRAND];
            this.swatchTypeOptions.push(brandOption);
            this.onSwatchTypeChange(brandOption);
        }
    }

    private loadSwatchTypePreference(): void {
        try {
            const saved = sessionStorage.getItem(COLOR_PICKER_SESSION_STORAGE_KEY);
            if (saved) {
                // Only use saved preference if it's a valid option
                if (saved === SwatchCollectionType.RECENT && this.showRecentColors) {
                    this.onSwatchTypeChange(this.drowdownFulloptions[SwatchCollectionType.RECENT]);
                } else if (saved === SwatchCollectionType.BRAND && this.brandId !== null && this.brandId !== undefined) {
                    this.onSwatchTypeChange(this.drowdownFulloptions[SwatchCollectionType.BRAND]);
                } else {
                    this.onSwatchTypeChange(this.drowdownFulloptions[SwatchCollectionType.FAVORITES]);
                }
            }
        } catch {
            // Session storage not available, use default
        }
    }

    private saveSwatchTypePreference(): void {
        try {
            sessionStorage.setItem(COLOR_PICKER_SESSION_STORAGE_KEY, this.selectedSwatchType);
        } catch {
            // Session storage not available
        }
    }

    private emitColorChange(source: ColorPickerSourceType): void {
        this.colorChange.emit({value: this.colorModel.hex, source});
    }

    private emitClose(closeInfo: CloseEvent) {
        this.close.emit(closeInfo);
    }

    private showMessage(severity: messageSeverityEnum, message: string) {
        const title = severity === messageSeverityEnum.success
            ? 'ColorPicker.favoriteColors.save.success.heading'
            : 'ColorPicker.favoriteColors.save.fail.heading';

        this.messageService.add({
            severity: severity,
            summary: title,
            detail: message
        });
    }

    onColorGradientChanged({value, source}: {value: ColorModel, source: ColorPickerSourceType}): void {
        this.colorModel = value;
        this.hexInputValue = value.hex;
        const closeRequestType = source === ColorPickerSourceType.GRADIENT ? ColorPickerCloseRequestType.GRADIENT_CLICK : ColorPickerCloseRequestType.HUE_CLICK;
        this.emitColorChange(source);
        this.emitClose({
            closeRequestType,
            needClose: false
        })
    }

    onSwatchColorSelected(color: string): void {
        this.colorModel = ColorModel.createFromHex(color);
        this.hexInputValue = this.colorModel.hex;
        this.emitColorChange(ColorPickerSourceType.SWATCH);
        this.emitClose({
            closeRequestType: ColorPickerCloseRequestType.SWATCH_CLICK,
            needClose: true
        })
    }

    onSwatchTypeChange(option: DropdownOptionModel): void {
        this.selectedSwatchModel = option;
        this.selectedSwatchType =  this.selectedSwatchModel.value;
    }

    onAddToFavoritesClick(): void {
        const color = this.colorModel.hex.toUpperCase();
        const hasSameColor = this.favoriteColors.includes(color);
        if (hasSameColor) {
            this.showMessage(messageSeverityEnum.error, 'ColorPicker.favoriteColors.save.fail.duplicate.message');
            return;
        }
        const hasReachedMaximum  = this.favoriteColors.length >= MAX_FAVORITE_COLORS;
        if (!hasReachedMaximum) {
            const newColors = [...this.favoriteColors, color];
            this.favoriteColors = newColors;
            this.favoriteColorsChange.emit(newColors);
        }
    }

    onManageFavoritesClick() {
        this.favoriteColorsService.openUserPreferencePage();
        this.emitClose({
            closeRequestType: ColorPickerCloseRequestType.MANAGE_FAVORITE_CLICK,
            needClose: true
        })
    }
}
